import { useMediaQuery } from '@chakra-ui/react';
import { useAtomValue } from 'jotai';
import { useState } from 'react';

import useErrorModal from '../components/errorModal/useErrorModal';
import {
  SigningErrorEnum,
  useLogSigningErrorMutation,
  useUserDealQuery,
} from '../gql/generated/graphql';
import { NEW_DEAL_VALUE, dashboardDealInfoAtom } from '../pages/dashboard/RootDashboard';
import { DealWithFarthestState } from '../pages/dashboard/utils';
import hotjar, { HotjarEvents } from '../services/analytics/hotjar';
import { RudderEvent, rudderanalytics } from '../services/analytics/rudderstack';
import { convertToLeaseEndTimeZone, getDatePart } from '../utils/dates';
import { DealStateEnum } from '../utils/types/deal';

export type ActiveModal = 'signing' | 'verify' | null;

const useStartSigning = (deal: DealWithFarthestState) => {
  const dealId = useAtomValue(dashboardDealInfoAtom);
  const isCobuyer = deal?.isCobuyer ?? false;
  const sentForSignatures = deal?.state === DealStateEnum.SentForSignatures;

  const [isTooSmall] = useMediaQuery('(max-width: 260px)');
  const [logSigningError] = useLogSigningErrorMutation();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [signingIsCobuyer, setSigningIsCobuyer] = useState(false);

  const { renderErrorModal, openErrorModal } = useErrorModal();
  const { renderErrorModal: renderScreenSizeErrorModal, openErrorModal: openScreenSizeErrorModal } =
    useErrorModal({
      title: 'Screen size not supported',
      buttonText: 'OK',
      description:
        'We apologize for this inconvenience. Document signing is not supported on devices with ' +
        'this screen size. Please switch to a device with a larger screen, ' +
        'such as a tablet or desktop.',
    });

  const isNewDeal = dealId?.id === NEW_DEAL_VALUE.id;
  const { data: dealData, refetch: refetchDeal } = useUserDealQuery({
    fetchPolicy: 'network-only',
    variables: {
      id: isNewDeal ? undefined : dealId?.id,
      isCobuyer: dealId?.isCobuyer,
    },
  });

  const handleSign = (signingAsCobuyer?: boolean) => {
    hotjar.track(HotjarEvents.OpenedSigning);
    const todaysDate = getDatePart(convertToLeaseEndTimeZone(new Date()));
    const generationDate = getDatePart(
      convertToLeaseEndTimeZone(deal?.r1_contract_generation_date_utc ?? new Date()),
    );

    if (!deal?.r1_contract_generation_date_utc || generationDate < todaysDate) {
      logSigningError({
        variables: {
          error: {
            deal_id: deal?.id,
            error_type: SigningErrorEnum.GenerationDate,
            error_message: 'Document generation date is in the past',
          },
        },
      });
      openErrorModal();
      return;
    }

    if (sentForSignatures) {
      rudderanalytics.track(RudderEvent.SigningExperienceOpened, {
        is_cobuyer: signingAsCobuyer ?? deal?.isCobuyer ?? false,
        deal_id: deal?.id || undefined,
      });
      setActiveModal('signing');
    }
  };

  const startSigning = (isCobuyerOverride?: boolean) => {
    if (isTooSmall) {
      openScreenSizeErrorModal();
      return;
    }

    const checkCobuyer = isCobuyerOverride ?? isCobuyer;
    setSigningIsCobuyer(checkCobuyer);
    const { verified: customerVerified } = dealData?.userDeal?.customer || {};
    const { verified: cobuyerVerified } = dealData?.userDeal?.cobuyer || {};
    if ((!checkCobuyer && !customerVerified) || (checkCobuyer && !cobuyerVerified)) {
      setActiveModal('verify');
      return;
    }

    handleSign(isCobuyerOverride);
  };

  return {
    startSigning,
    activeModal,
    setActiveModal,
    refetchDeal,
    signingIsCobuyer,
    renderErrorModals: (
      <>
        {renderErrorModal}
        {renderScreenSizeErrorModal}
      </>
    ),
  };
};

export default useStartSigning;
